<script setup lang="ts">
interface Props {
  show: boolean
  title?: string
  size?: 'sm' | 'md' | 'lg' | 'xl'
  /** Shows above other open dialogs (used by the identity check, which can interrupt any of them). */
  elevated?: boolean
}

withDefaults(defineProps<Props>(), {
  size: 'md',
})

const emit = defineEmits<{
  (e: 'close'): void
}>()

// Escape closes the dialog, like clicking the backdrop.
function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') emit('close')
}

const sizes = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
}
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="show"
        :class="elevated ? 'z-[70]' : 'z-50'"
        class="fixed inset-0 overflow-y-auto"
        role="dialog"
        aria-modal="true"
        :aria-label="title"
        @keydown="onKeydown"
      >
        <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
          <div
            class="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
            @click="emit('close')"
          ></div>

          <div
            :class="[
              sizes[size],
              'relative inline-block w-full bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle',
            ]"
          >
            <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div class="flex items-start justify-between mb-4">
                <h3 v-if="title" class="text-lg font-medium text-gray-900">
                  {{ tr(title) }}
                </h3>
                <button class="text-gray-400 hover:text-gray-600" @click="emit('close')">
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div class="mt-2">
                <slot />
              </div>
            </div>

            <div v-if="$slots.footer" class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <slot name="footer" />
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
</style>
